// src/pages/DevicesPage.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { devices as devicesApi } from '../utils/api';
import ConfirmModal from '../components/ConfirmModal';
import Pagination, { usePagination } from '../components/Pagination';

const DEV_STATUS = { enabled: 'badge-success', disabled: 'badge-danger', removed: 'badge-muted' };
const TYPE_ICON  = { laptop: '💻', phone: '📱', tablet: '📟', other: '🔌' };

export default function DevicesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const deleteMut = useMutation({
    mutationFn: (id) => devicesApi.delete(id),
    onSuccess: () => { toast.success('Device deleted'); qc.invalidateQueries(['devices']); setConfirmDelete(null); },
    onError: err => { toast.error(err.response?.data?.message || 'Failed to delete'); setConfirmDelete(null); }
  });

  const { data: raw, isLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list().then(r => r.data.data)
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }) => devicesApi.setStatus(id, status),
    onSuccess: () => { toast.success('Status updated'); qc.invalidateQueries(['devices']); },
    onError: err => toast.error(err.response?.data?.message || 'Failed')
  });

  const allFiltered = (raw || []).filter(d => {
    const q = search.toLowerCase();
    const matchSearch = !q || d.mac_address.toLowerCase().includes(q) ||
      d.student_name?.toLowerCase().includes(q) ||
      d.registration_no?.toLowerCase().includes(q) ||
      d.device_model?.toLowerCase().includes(q) ||
      d.vendor_name?.toLowerCase().includes(q);
    const matchStatus = !statusFilter || d.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const { paginated, page, setPage, totalPages, total } = usePagination(allFiltered, 10);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Devices</h1>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          {total} device{total !== 1 ? 's' : ''} found
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-input">
          <span className="search-icon">🔍</span>
          <input className="form-control" placeholder="Search MAC, student, model, vendor…"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ paddingLeft: 34 }} />
        </div>
        <select className="form-control" style={{ maxWidth: 160 }} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="enabled">Enabled</option>
          <option value="disabled">Disabled</option>
          <option value="removed">Removed</option>
        </select>
      </div>

      <div className="card">
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><span className="spinner" /></div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Type</th><th>MAC Address</th><th>Model</th><th>Vendor</th>
                    <th>Student</th><th>Reg No</th><th>Status</th><th>Registered</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 && (
                    <tr><td colSpan={9}><div className="empty-state"><div className="icon">📱</div>No devices found</div></td></tr>
                  )}
                  {paginated.map(d => (
                    <tr key={d.id}>
                      <td style={{ fontSize: '1.2rem' }}>{TYPE_ICON[d.device_type] || '🔌'}</td>
                      <td><span className="text-mono">{d.mac_address}</span></td>
                      <td>{d.device_model || '—'}</td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{d.vendor_name || '—'}</td>
                      <td>
                        <Link to={`/students/${d.student_id}`} style={{ fontWeight: 600 }}>{d.student_name}</Link>
                      </td>
                      <td><span className="text-mono" style={{ fontSize: '0.75rem' }}>{d.registration_no}</span></td>
                      <td><span className={`badge ${DEV_STATUS[d.status]}`}>{d.status}</span></td>
                      <td className="text-mono">{new Date(d.created_at).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {d.status === 'enabled' ? (
                            <button className="btn btn-danger btn-sm"
                              onClick={() => statusMut.mutate({ id: d.id, status: 'disabled' })}>
                              Disable
                            </button>
                          ) : d.status === 'disabled' ? (
                            <button className="btn btn-success btn-sm"
                              onClick={() => statusMut.mutate({ id: d.id, status: 'enabled' })}>
                              Enable
                            </button>
                          ) : null}
                          <Link to={`/devices/${d.id}`} className="btn btn-secondary btn-sm">Details</Link>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => setConfirmDelete(d)}
                            disabled={d.status === 'enabled'}
                            title={d.status === 'enabled' ? 'Disable device before deleting' : 'Delete'}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>

      {confirmDelete && (
        <ConfirmModal
          title="Delete Device"
          message={`Are you sure you want to delete device ${confirmDelete.mac_address}? This cannot be undone.`}
          loading={deleteMut.isPending}
          onConfirm={() => deleteMut.mutate(confirmDelete.id)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
